import { useMsal } from '@azure/msal-react';
import axios, { AxiosError } from 'axios';
import save from 'save-file';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Flex, Paper, ScrollArea, Skeleton, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    getApiDocumentsIdDocx,
    useGetApiDocumentsId,
    usePutApiDocumentsId
} from '../../api/documents/documents.ts';
import { useEditorOperations } from '../../hooks/useEditorOperations';
import getAccessToken from '../auth/GetAccessToken';
import { LoadingFallBack } from '../utils/LoadingFallBack.tsx';
import ServerLoadErrorFallback from '../utils/ServerLoadErrorFallback.tsx';
import { useProjectData } from '../utils/useProjectData.tsx';
import { DocumentOutlinePanel, DocumentOutlineSkeleton } from './DocumentOutlinePanel.tsx';
import MilkdownEditorComponent, { type CollabConfig, type CollabStatus } from './milkdown/MilkdownEditor';
import { MilkdownAdapter } from './milkdown/milkdown-adapter';
import BpmnGeneratorModal, { BpmnGeneratorModalRef } from './plugins/BPMN/BpmnGeneratorModal.tsx';

const collabWsUrl = import.meta.env.VITE_COLLAB_WS_URL as string | undefined;

const EditorView = () => {
    const { documentId } = useParams();
    const { refetchProjects } = useProjectData();
    const {
        data: document,
        error,
        isLoading,
        refetch: refetchDocument
    } = useGetApiDocumentsId(documentId ?? '');
    const { mutateAsync: saveDocument, error: errorSaving } =
        usePutApiDocumentsId();

    const [adapter] = useState(() => new MilkdownAdapter());
    useEffect(() => () => adapter.destroy(), [adapter]);
    const { insertImage, getContent } = useEditorOperations(adapter);

    // Collaboration config — always required
    const { instance: msalInstance } = useMsal();
    const [collabConfig, setCollabConfig] = useState<CollabConfig | undefined>();
    const collabConfigDocRef = useRef<string | null>(null);

    // Reset collab config when navigating to a different document so the
    // loading state shows while the new config is built.
    useEffect(() => {
        setCollabConfig(undefined);
        collabConfigDocRef.current = null;
    }, [documentId]);

    useEffect(() => {
        if (!collabWsUrl || !documentId || !document) return;
        // Only build collabConfig once per documentId — subsequent refetches of
        // `document` (e.g. window focus) must not recreate the config because
        // Yjs is the source of truth once the session is established.
        if (collabConfigDocRef.current === documentId) return;
        collabConfigDocRef.current = documentId;

        const account = msalInstance.getActiveAccount();
        const userName = account?.name ?? account?.username ?? 'Anonymous';

        // Generate a deterministic colour from the user's name
        const hue = userName.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;

        setCollabConfig({
            wsUrl: collabWsUrl,
            documentName: documentId,
            // Pass a function so HocuspocusProvider fetches a fresh token on every
            // (re)connect, avoiding stale-token auth failures after ~60min.
            token: async () => (await getAccessToken(msalInstance)) ?? '',
            user: { name: userName, color: `hsl(${String(hue)}, 70%, 50%)` },
            initialContent: document.content
        });
    }, [documentId, document, msalInstance]);

    // Detect involuntary disconnection (e.g. MCP closed the room) and
    // immediately tear down the editor, refetch fresh content, and remount.
    // Acting on 'disconnected' rather than waiting for reconnect avoids the
    // provider's exponential backoff delay and prevents typing collisions.
    const hadInitialConnectRef = useRef(false);
    const handleCollabStatusChange = useCallback(
        (status: CollabStatus) => {
            if (status === 'connected') {
                hadInitialConnectRef.current = true;
                return;
            }

            if (status === 'disconnected' && hadInitialConnectRef.current) {
                // Tear down immediately — the old editor unmounts (destroying
                // the stale provider), refetch gets fresh content, and the
                // collabConfig rebuild remounts a new editor with a clean connection.
                collabConfigDocRef.current = null;
                setCollabConfig(undefined);
                void refetchDocument();
            }
        },
        [refetchDocument]
    );

    const [scrollAreaViewport, setScrollAreaViewport] = useState<HTMLDivElement | null>(null);
    const [reinitializeTrigger, setReinitializeTrigger] = useState(0);
    const handleContentChange = useCallback(
        () => setReinitializeTrigger(c => c + 1),
        []
    );

    const replaceCodeSuffix = (code: string, newSuffix: string): string => {
        const parts = code.split('-');
        if (parts.length !== 3) return code;
        return `${parts[0]}-${parts[1]}-${newSuffix.padStart(3, '0')}`;
    };

    // Quiet auto-save — no notifications, no refetch before save
    const autoSave = useCallback(async (content: string) => {
        if (!document) return;

        await saveDocument({
            id: documentId!,
            data: {
                content,
                name: document.name,
                title: document.title,
                titlePage: document.titlePage,
                documentNumber: document.documentNumber,
                template: document.template,
                markdownTemplate: document.markdownTemplate,
                notionPhaseId: document.notionPhaseId,
                documentCode: replaceCodeSuffix(
                    document.documentCode ?? '',
                    document.documentNumber
                )
            }
        });
    }, [document, documentId, saveDocument]);

    // Full save used by DOCX export (saves + refetches to get latest doc metadata)
    const saveForExport = async (content: string) => {
        const { data: latestDoc } = await refetchDocument();

        await saveDocument({
            id: documentId!,
            data: {
                content,
                name: latestDoc!.name,
                title: latestDoc!.title,
                titlePage: latestDoc!.titlePage,
                documentNumber: latestDoc!.documentNumber,
                template: latestDoc!.template,
                markdownTemplate: latestDoc!.markdownTemplate,
                notionPhaseId: latestDoc!.notionPhaseId,
                documentCode: replaceCodeSuffix(
                    latestDoc?.documentCode ?? '',
                    latestDoc!.documentNumber
                )
            }
        });
        await refetchDocument();
        await refetchProjects();

        return latestDoc;
    };

    const exportToDocX = async (content?: string) => {
        const id = notifications.show({
            title: 'Exporting',
            message: 'Exporting document to DOCX',
            loading: true,
            withCloseButton: false,
            autoClose: false
        });

        try {
            const contentToExport = content ?? getContent();
            const latestDoc = await saveForExport(contentToExport);
            const file = await getApiDocumentsIdDocx(documentId!);

            // void used here instead of await as await removes focus from window and prevents the notification from updating.
            void save(
                file,
                `${latestDoc?.documentCode ?? ''} ${latestDoc!.name}.docx`
            );

            notifications.update({
                id,
                loading: false,
                autoClose: 2000,
                withCloseButton: true,
                title: 'Exported',
                message: 'Export complete!'
            });
        } catch (error) {
            let errorMessage = 'An unexpected error occurred';

            if (axios.isAxiosError(error) && error.response) {
                const { status, statusText } = error.response;
                errorMessage = `Server error: ${status} ${statusText}`;

                // Blob responses need to be read as text to extract error details
                if (error.response.data instanceof Blob) {
                    try {
                        const text = await error.response.data.text();
                        const parsed = JSON.parse(text) as {
                            title?: string;
                            detail?: string;
                        };
                        if (parsed.title || parsed.detail) {
                            errorMessage =
                                parsed.detail ?? parsed.title ?? errorMessage;
                        }
                    } catch {
                        // Couldn't parse blob — use the status message
                    }
                }
            }

            notifications.update({
                id,
                loading: false,
                autoClose: 5000,
                withCloseButton: true,
                title: 'Export Failed',
                message: errorMessage,
                color: 'red'
            });
        }
    };

    const bpmnGeneratorModalRef = useRef<BpmnGeneratorModalRef>(null);

    const openBpmnGenerator = () => {
        bpmnGeneratorModalRef.current?.openModal();
    };

    const handleBpmnImageSelected = (image: string) => {
        insertImage(image, 'BPMN Diagram');
    };

    if (isLoading || !document)
        return <LoadingFallBack message={'Loading document'} wrapInMaxWidth />;
    if (error || errorSaving)
        return (
            <ServerLoadErrorFallback
                entity={'Document View'}
                error={(error ?? errorSaving) as AxiosError}
            />
        );

    if (!collabWsUrl) {
        return (
            <Alert color="red" title="Configuration Error" m="md">
                <Text size="sm">
                    Collaboration server URL (VITE_COLLAB_WS_URL) is not configured.
                    The editor requires a running Hocuspocus server.
                </Text>
            </Alert>
        );
    }

    // Wait for collabConfig to be built — show a skeleton that approximates the
    // editor + outline layout so the transition feels seamless.
    if (!collabConfig) {
        const fullHeight = 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - var(--mantine-spacing-xs, 8px) * 2)';
        return (
            <Flex
                gap='md'
                style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', minHeight: fullHeight }}
            >
                <Paper
                    shadow='xl'
                    radius='xs'
                    withBorder
                    style={{ flex: 1, minWidth: 0, borderTop: 'none' }}
                    p='md'
                >
                    {/* Toolbar skeleton */}
                    <Skeleton height={36} mb='md' />
                    {/* Content lines */}
                    <Stack gap='sm' mt='lg'>
                        <Skeleton height={12} width='70%' />
                        <Skeleton height={12} width='90%' />
                        <Skeleton height={12} width='55%' />
                        <Skeleton height={12} width='80%' />
                        <Skeleton height={12} width='65%' />
                        <Skeleton height={12} width='85%' mt='md' />
                        <Skeleton height={12} width='40%' />
                        <Skeleton height={12} width='75%' />
                    </Stack>
                </Paper>
                <Paper
                    shadow='sm'
                    radius='xs'
                    withBorder
                    w={250}
                    style={{ alignSelf: 'flex-start', height: fullHeight }}
                >
                    <DocumentOutlineSkeleton />
                </Paper>
            </Flex>
        );
    }

    return (
        <ScrollArea
            h='calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - var(--mantine-spacing-xs, 8px) * 2)'
            viewportRef={setScrollAreaViewport}
            style={{ width: '100%' }}
        >
            <Flex
                gap='md'
                miw={'80'}
                style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    width: '100%',
                    minHeight: 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - var(--mantine-spacing-xs, 8px) * 2)'
                }}
            >
                <Paper
                    shadow='xl'
                    radius='xs'
                    withBorder
                    style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', borderTop: 'none' }}
                >
                    <MilkdownEditorComponent
                        key={documentId}
                        adapter={adapter}
                        data={document}
                        saveDoc={autoSave}
                        exportDocX={exportToDocX}
                        openBpmnGenerator={openBpmnGenerator}
                        onContentChange={handleContentChange}
                        collabConfig={collabConfig}
                        onCollabStatusChange={handleCollabStatusChange}
                    />
                    <BpmnGeneratorModal
                        ref={bpmnGeneratorModalRef}
                        onInsert={handleBpmnImageSelected}
                    />
                </Paper>
                <Paper
                        shadow='sm'
                        radius='xs'
                        withBorder
                        w={250}
                        style={{
                            position: 'sticky',
                            top: 0,
                            alignSelf: 'flex-start',
                            height: 'calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - var(--mantine-spacing-xs, 8px) * 2)'
                        }}
                    >
                        <DocumentOutlinePanel
                            scrollAreaViewport={scrollAreaViewport}
                            reinitializeTrigger={reinitializeTrigger}
                        />
                    </Paper>
            </Flex>
        </ScrollArea>
    );
};

/** Thin wrapper that keys EditorView on documentId so the entire component
 *  remounts when navigating between documents — all state (adapter, collab
 *  connection, auto-save timers) is cleanly torn down and recreated. */
const EditorViewKeyed = () => {
    const { documentId } = useParams();
    return <EditorView key={documentId} />;
};

export default EditorViewKeyed;
